import React, { useState, useEffect } from 'react';
import { Team } from '../types/Team';
import { deleteTeam } from '../api/apis';

interface TeamMetadataProps {
  team: Team;
  onUpdate: (updatedTeam: Team) => void;
  editable?: boolean;
  isCreating?: boolean;
  onDelete?: () => void;
}

export const TeamMetadata = ({ team, onUpdate, editable = false, isCreating = false, onDelete }: TeamMetadataProps) => {
  const [isEditing, setIsEditing] = useState(isCreating);
  const [editedTeam, setEditedTeam] = useState<Team | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const categories = ['Male', 'Female', 'Mixed'];

  useEffect(() => {
    setEditedTeam(team);
    setIsEditing(isCreating);
  }, [team, isCreating]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this team?')) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteTeam(team.id);
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('Failed to delete team:', error);
      alert('Failed to delete team. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTeamUpdate = (updates: Partial<Team>) => {
    if (!editedTeam) return;
    setEditedTeam({ ...editedTeam, ...updates });
  };

  const handleMemberUpdate = (index: number, name: string) => {
    if (!editedTeam) return;
    const newMembers = [...editedTeam.members];
    newMembers[index] = { name };
    handleTeamUpdate({ members: newMembers });
  };

  const handleMemberRemove = (index: number) => {
    if (!editedTeam) return;
    const newMembers = editedTeam.members.filter((_, i) => i !== index);
    handleTeamUpdate({ members: newMembers });
  };

  const handleAddMember = () => {
    if (!editedTeam) return;
    handleTeamUpdate({ members: [...editedTeam.members, { name: '' }] });
  };

  const renderEditForm = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700">Team Name</label>
        <input
          type="text"
          value={editedTeam?.name}
          onChange={(e) => handleTeamUpdate({ name: e.target.value })}
          className="w-full border border-gray-200 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700">Category</label>
        <select
          value={editedTeam?.category}
          onChange={(e) => handleTeamUpdate({ category: e.target.value })}
          className="w-full border border-gray-200 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        >
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-sm font-semibold mb-2 text-gray-700">Team Members</label>
        {editedTeam?.members.map((member, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={member.name}
              onChange={(e) => handleMemberUpdate(index, e.target.value)}
              className="flex-1 border border-gray-200 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
            <button
              onClick={() => handleMemberRemove(index)}
              className="text-red-500 px-2"
            >
              ×
            </button>
          </div>
        ))}
        <button
          onClick={handleAddMember}
          className="text-blue-500 text-sm"
        >
          + Add Member
        </button>
      </div>

      <div className="flex gap-3 mt-6">
        <button
          onClick={() => {
            if (editedTeam) onUpdate(editedTeam);
            setIsEditing(false);
          }}
          className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors flex-1"
        >
          Save
        </button>
        <button
          onClick={() => {
            setIsEditing(false);
            setEditedTeam(null);
          }}
          className="border border-gray-200 hover:bg-gray-50 px-6 py-2 rounded-lg transition-colors flex-1"
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const renderViewMode = () => (
    <div className="grid grid-cols-2 gap-6">
      <div className="col-span-2 bg-gray-50 rounded-lg p-4">
        <div className="text-sm text-gray-500 mb-1">Team Name</div>
        <div className="text-lg font-semibold text-gray-800">{editedTeam?.name}</div>
      </div>
      
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="text-sm text-gray-500 mb-1">Category</div>
        <div className="text-lg font-semibold text-gray-800">{editedTeam?.category}</div>
      </div>
      
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="text-sm text-gray-500 mb-1">Start Heat</div>
        <div className="text-lg font-semibold text-gray-800">
         {editedTeam?.startHeat ?? '-'}
        </div>
      </div>
      
      <div className="col-span-2 bg-gray-50 rounded-lg p-4">
        <div className="text-sm text-gray-500 mb-2">Team Members</div>
        <div className="grid grid-cols-2 gap-2">
          {editedTeam?.members.map((member, index) => (
            <div key={index} className="bg-white rounded-md p-2 text-gray-800">
              {member.name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Team Information</h2>
        {editable && !isEditing && (
          <div className="flex gap-2">
            <button
              onClick={() => setIsEditing(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        )}
      </div>
      
      {isEditing ? renderEditForm() : renderViewMode()}
    </div>
  );
};

export type { TeamMetadataProps };
